import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateHyperlink } from '../tool';
import { addQuestion } from '../services/questionService';
import useUserContext from './useUserContext';
import { Question, Tag } from '../types/types';

/**
 * Custom hook to handle question submission and form validation
 *
 * @returns title - The current value of the title input.
 * @returns text - The current value of the text input.
 * @returns tagNames - The current value of the tags input.
 * @returns tags - Array of Tag objects derived from tagNames.
 * @returns titleErr - Error message for the title field, if any.
 * @returns textErr - Error message for the text field, if any.
 * @returns tagErr - Error message for the tag field, if any.
 * @returns postQuestion - Function to validate the form and submit a new question.
 * @returns addTag - Function to add a new tag
 * @returns removeTag - Function to remove a tag
 * @returns lastQuestionId - ID of the last successfully posted question
 */
const useNewQuestion = () => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [title, setTitle] = useState<string>('');
  const [text, setText] = useState<string>('');
  const [tagNames, setTagNames] = useState<string>('');
  const [lastQuestionId, setLastQuestionId] = useState<string | null>(null);

  const [titleErr, setTitleErr] = useState<string>('');
  const [textErr, setTextErr] = useState<string>('');
  const [tagErr, setTagErr] = useState<string>('');

  // Convert space-separated tags to array of Tag objects
  const tags: Tag[] = useMemo(
    () =>
      tagNames
        .split(' ')
        .filter(name => name.trim() !== '')
        .map(name => ({
          name,
          description: 'user added tag',
        })),
    [tagNames],
  );

  /**
   * Add a new tag to the list
   */
  const addTag = (newTag: string) => {
    if (!newTag || newTag.trim() === '') return;

    // Check if tag already exists
    if (tags.some(tag => tag.name === newTag)) return;

    // Check if adding this would exceed max tags
    if (tags.length >= 5) {
      setTagErr('Cannot have more than 5 tags');
      return;
    }

    // Check tag length
    if (newTag.length > 20) {
      setTagErr('New tag length cannot be more than 20');
      return;
    }

    // Add the tag
    const newTagNames = tagNames ? `${tagNames} ${newTag}` : newTag;
    setTagNames(newTagNames);
    setTagErr('');
  };

  /**
   * Remove a tag from the list
   */
  const removeTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag.name !== tagToRemove);
    setTagNames(updatedTags.map(tag => tag.name).join(' '));
  };

  /**
   * Function to validate the form before submitting the question.
   *
   * @returns boolean - True if the form is valid, false otherwise.
   */
  const validateForm = (): boolean => {
    let isValid = true;

    if (!title) {
      setTitleErr('Title cannot be empty');
      isValid = false;
    } else if (title.length > 100) {
      setTitleErr('Title cannot be more than 100 characters');
      isValid = false;
    } else {
      setTitleErr('');
    }

    if (!text) {
      setTextErr('Question text cannot be empty');
      isValid = false;
    } else if (!validateHyperlink(text)) {
      setTextErr('Invalid hyperlink format.');
      isValid = false;
    } else {
      setTextErr('');
    }

    if (tags.length === 0) {
      setTagErr('Should have at least 1 tag');
      isValid = false;
    } else if (tags.length > 5) {
      setTagErr('Cannot have more than 5 tags');
      isValid = false;
    } else {
      setTagErr('');
    }

    for (const tag of tags) {
      if (tag.name.length > 20) {
        setTagErr('New tag length cannot be more than 20');
        isValid = false;
        break;
      }
    }

    return isValid;
  };

  /**
   * Function to post a question to the server.
   * After successful submission, navigates to the new question page.
   */
  const postQuestion = async () => {
    if (!validateForm()) return;

    const question: Question = {
      title,
      text,
      tags,
      askedBy: user.username,
      askDateTime: new Date(),
      answers: [],
      upVotes: [],
      downVotes: [],
      views: [],
      comments: [],
    };

    try {
      const res = await addQuestion(question);

      if (res && res._id) {
        // Store the ID for possible future use
        const questionId = String(res._id);
        setLastQuestionId(questionId);

        // Navigate to the newly created question
        navigate(`/question/${questionId}`);
      }
    } catch (error) {
      //
    }
  };

  return {
    title,
    setTitle,
    text,
    setText,
    tagNames,
    setTagNames,
    tags,
    addTag,
    removeTag,
    titleErr,
    textErr,
    tagErr,
    postQuestion,
    lastQuestionId,
  };
};

export default useNewQuestion;
